<script setup lang="ts">
import { computed, ref } from '#imports';
import { formProps } from '../formProps';

const model = defineModel<boolean | 'indeterminate'>({
  default: false,
});

const props = defineProps({
  switch: {
    type: Boolean,
    default: false,
  },
  ...formProps,
  link: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  ...formProps,
});

// Computed to handle the state check
const isChecked = computed(() => model.value === true);
const isIndeterminate = computed(() => model.value === 'indeterminate');

// Toggle the checkbox state
const toggleCheckbox = () => {
  if (props.disabled) return;

  // For regular checkbox, cycle through states
  if (!props.switch) {
    if (model.value === 'indeterminate') {
      model.value = true;
    } else if (model.value === true) {
      model.value = false;
    } else {
      model.value = true;
    }
  } else {
    // For switch, just toggle between true/false
    model.value = !model.value;
  }
};

// Set indeterminate state on native input
const checkboxRef = ref<HTMLInputElement | null>(null);
const updateNativeState = () => {
  if (checkboxRef.value) {
    checkboxRef.value.indeterminate = model.value === 'indeterminate';
    checkboxRef.value.checked = model.value === true;
  }
};
</script>

<template>
  <ClientOnly>
    <div class="root" :class="{ 'is-switch': props.switch }">
      <!-- Checkbox implementation -->
      <div v-if="!props.switch" class="checkbox-wrapper" @click="toggleCheckbox">
        <div class="checkbox-control" :class="{
          'is-checked': isChecked,
          'is-indeterminate': isIndeterminate
        }">
          <i v-if="isChecked" class="ri-checkbox-line"></i>
          <i v-if="isIndeterminate" class="ri-checkbox-fill"></i>
        </div>
        <label v-if="!props.hideLabel" class="control-label">
          <span>{{ props.label }}</span>

        </label>
        <NuxtLink
          v-if="props.link"
          :to="props.link"
          class="label-link"
        >
          {{ linkText || 'Learn more' }}
        </NuxtLink>
        <input
          ref="checkboxRef"
          type="checkbox"
          class="hidden-input"
          :checked="isChecked"
          @change="toggleCheckbox"
          @update="updateNativeState"
        >
      </div>

      <!-- Switch implementation -->
      <div v-if="props.switch" class="switch-wrapper" @click="toggleCheckbox">
        <div class="switch-control" :class="{ 'is-checked': isChecked }">
          <div class="switch-thumb"></div>
        </div>
        <label v-if="!props.hideLabel" class="control-label">
          <span>{{ props.label }}</span>

        </label>
        <NuxtLink
          v-if="props.link"
          :to="props.link"
          class="label-link"
        >
          {{ linkText || 'Learn more' }}
        </NuxtLink>
        <input
          type="checkbox"
          class="hidden-input"
          :checked="isChecked"
          @change="toggleCheckbox"
        >
      </div>

      <small v-if="props.helpText" class="help-text">{{ props.helpText }}</small>
    </div>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/input.pcss";

.root {
  width: 100%;
  display: block;
  text-align: left;
  position: relative;
}

.root.is-switch {
  width: 100%;
  display: flex;
  text-align: left;
  position: relative;
  align-items: start;
  flex-direction: column;
}

.checkbox-wrapper, .switch-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-control {
  position: relative;
  width: 1.25em;
  height: 1.25em;
  background-color: var(--main-background-color);
  border-radius: 0.5rem;
  box-shadow: 0 0 0 1px var(--color-slate-200), 0 2px 2px 0 var(--color-slate-100);
  transition: transform 0.3s;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-label {
  @apply mp-font-sm;
  font-weight: 400;
  margin: 0 0 0 0.5rem;
  color: var(--color-slate-600);
  white-space: nowrap;
}

.switch-control {
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s;
  width: 2.25rem;
  height: 1.25rem;
  background-color: var(--color-slate-100);
  border: 1px solid var(--color-slate-200);
  border-radius: 1rem;
  cursor: pointer;
}

.switch-thumb {
  width: 1rem;
  height: 1rem;
  background-color: var(--color-slate-300);
  border-radius: 50%;
  transition: transform 0.3s;
  box-shadow: var(--shadow-base);
  margin-left: 0.1rem;
}

.switch-control.is-checked {
  background-color: var(--color-green-400);
  border-color: var(--color-green-500);
}

.switch-control.is-checked .switch-thumb {
  transform: translateX(1rem);
  background-color: var(--main-background-color);
}

.hidden-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.help-text {
  @mixin mp-helper-text;
  margin-top: 0.5rem;
  display: block;
}

.label-link {
  color: var(--color-indigo-400);
  margin-left: 0.5rem;
}

</style>
